import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const ShopInfo = ({ shopId }) => {
  const [shopData, setShopData] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPhotos: 0,
    totalStamps: 0,
    averageStampsPerUser: 0,
    pendingPhotos: 0,
    approvedPhotos: 0,
    totalStampCards: 0
  });
  const [loading, setLoading] = useState(true);
  const [indexBuilding, setIndexBuilding] = useState(false);
  const [indexUrl, setIndexUrl] = useState("");
  const [retryIn, setRetryIn] = useState(0);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (shopId) {
      loadShopInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, reloadTick]);

  const loadShopInfo = async () => {
    try {
      setLoading(true);
      setIndexBuilding(false);

      // Load shop data
      const shopDoc = await getDoc(doc(db, 'shops', shopId));
      if (shopDoc.exists()) {
        setShopData(shopDoc.data());
      }

      // Load customers for this shop (from shopCustomers collection)
      const shopCustomersQuery = query(
        collection(db, 'shopCustomers'),
        where('shopId', '==', shopId)
      );
      const shopCustomersSnapshot = await getDocs(shopCustomersQuery);
      const totalUsers = shopCustomersSnapshot.size;

      // Load photos for this shop
      const photosQuery = query(
        collection(db, 'photos'),
        where('shopId', '==', shopId)
      );
      const photosSnapshot = await getDocs(photosQuery);
      const totalPhotos = photosSnapshot.size;
      
      let pendingPhotos = 0;
      let approvedPhotos = 0;
      photosSnapshot.forEach(doc => {
        const photoData = doc.data();
        if (photoData.status === 'pending') {
          pendingPhotos++;
        } else if (photoData.status === 'approved') {
          approvedPhotos++;
        }
      });

      // Calculate total stamps from shopCustomers
      let totalStamps = 0;
      shopCustomersSnapshot.forEach(doc => {
        const customerData = doc.data();
        totalStamps += customerData.stamps || 0;
      });
      
      const totalStampCards = totalUsers; // Each customer has one stamp card

      const averageStampsPerUser = totalUsers > 0 ? (totalStamps / totalUsers).toFixed(1) : 0;

      setStats({
        totalUsers,
        totalPhotos,
        totalStamps,
        averageStampsPerUser,
        pendingPhotos,
        approvedPhotos,
        totalStampCards
      });

      setIndexBuilding(false);
      setIndexUrl("");
      setRetryAttempts(0);

    } catch (error) {
      console.error('Error loading shop info:', error);
      const msg = error?.message || "";
      const isIndexIssue = error?.code === 'failed-precondition' || msg.includes('requires an index') || msg.includes('index is currently building');
      if (isIndexIssue) {
        const m = msg.match(/https?:\/\/\S+/);
        setIndexUrl(m ? m[0] : "");
        setIndexBuilding(true);
        const attempts = retryAttempts + 1;
        setRetryAttempts(attempts);
        const waitSec = Math.min(30, 5 * attempts);
        setRetryIn(waitSec);
        let remaining = waitSec;
        const timer = setInterval(() => {
          remaining -= 1;
          setRetryIn(prev => (prev > 0 ? prev - 1 : 0));
          if (remaining <= 0) clearInterval(timer);
        }, 1000);
        if (attempts <= 6) {
          setTimeout(() => setReloadTick(t => t + 1), waitSec * 1000);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card card-flat portal-panel">
        <div className="card-body text-center">
          <div className="loading-spinner" style={{ margin: '0 auto 20px' }} />
          <div role="status" aria-live="polite">Laden...</div>
        </div>
      </div>
    );
  }

  if (!shopId) {
    return (
      <div className="card card-flat portal-panel">
        <div className="card-body text-center">
          <h3>Geen winkel geselecteerd</h3>
          <p>Er is geen geldige winkel gevonden.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card card-flat portal-panel">
      <div className="card-body">
      <h2 style={{
        marginBottom: '24px',
        color: 'var(--ink-strong)',
        fontSize: '1.4rem',
        fontWeight: 'bold'
      }}>
        📊 Winkel Informatie
      </h2>

      {/* Shop Details */}
      {shopData && (
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '24px'
        }}>
          <h3 style={{ marginBottom: '12px', color: '#2c3e50' }}>Winkel Details</h3>
          {indexBuilding && (
            <div role="status" aria-live="polite" style={{ padding: '8px 12px', background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba', borderRadius: 8, marginBottom: 12 }}>
              {indexUrl ? (<a href={indexUrl} target="_blank" rel="noreferrer">Open index status</a>) : 'Index aan het bouwen...'}
              <span style={{ marginLeft: 6 }}>
                {retryAttempts <= 6 ? `Automatische poging over ${retryIn}s.` : 'Automatische pogingen gestopt.'}
              </span>
              <button onClick={loadShopInfo} className="btn btn-secondary" style={{ marginLeft: 10, padding: '2px 8px' }}>Opnieuw proberen</button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <strong>Naam:</strong> {shopData.name || 'Niet ingesteld'}
            </div>
            <div>
              <strong>Locatie:</strong> {shopData.location || 'Niet ingesteld'}
            </div>
            <div>
              <strong>Max Stamps:</strong> {shopData.maxStamps || 10}
            </div>
            <div>
              <strong>Aangemaakt:</strong> {shopData.createdAt ? new Date(shopData.createdAt.toDate()).toLocaleDateString('nl-NL') : 'Onbekend'}
            </div>
          </div>
        </div>
      )}

      {/* Statistics Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: '#e3f2fd',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #2196f3'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2196f3' }}>
            👥 {stats.totalUsers}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Totaal Gebruikers
          </div>
        </div>

        <div style={{
          backgroundColor: '#e8f5e8',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #4caf50'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4caf50' }}>
            📸 {stats.totalPhotos}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Totaal Foto's
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff3e0',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #ff9800'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff9800' }}>
            🏷️ {stats.totalStamps}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Totaal Stamps
          </div>
        </div>

        <div style={{
          backgroundColor: '#f3e5f5',
          padding: '20px',
          borderRadius: '12px',
          textAlign: 'center',
          border: '2px solid #9c27b0'
        }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9c27b0' }}>
            📊 {stats.averageStampsPerUser}
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
            Gem. per Gebruiker
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '16px'
      }}>
        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: '12px', color: '#2c3e50' }}>📋 Stamp Cards</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50' }}>
            {stats.totalStampCards}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Actieve stamp cards
          </div>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: '12px', color: '#2c3e50' }}>⏳ Wachtende Foto's</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ff9800' }}>
            {stats.pendingPhotos}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Nog te modereren
          </div>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: '12px', color: '#2c3e50' }}>✅ Goedgekeurde Foto's</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#4caf50' }}>
            {stats.approvedPhotos}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Gepubliceerd
          </div>
        </div>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ marginBottom: '12px', color: '#2c3e50' }}>📈 Conversie</h4>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2196f3' }}>
            {stats.totalUsers > 0 ? ((stats.totalStampCards / stats.totalUsers) * 100).toFixed(1) : 0}%
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Gebruikers met stamp cards
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div style={{ textAlign: 'center', marginTop: '24px' }}>
        <button
          onClick={loadShopInfo}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: 'bold'
          }}
        >
          🔄 Vernieuwen
        </button>
      </div>
      </div>
    </div>
  );
};

export default ShopInfo; 